import { Button as HeadlessButton } from "@headlessui/react";
import { clsx } from "clsx";
import React, { ForwardedRef, ReactNode } from "react";
import Spinner from "./spinner";

export type ButtonProps = {
  className?: string;
  type?: "submit" | "button";
  loading?: boolean;
  renderIcon?: () => ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  disabled?: boolean;
  variant?: "primary" | "secondary" | "danger";

  onClick?: () => void;
} & React.PropsWithChildren;

const styles = {
  base: "flex items-center gap-x-2 border-none focus:outline-none transition ease-in-out duration-300 rounded-md",
  disabled: "opacity-50 cursor-not-allowed",
  pill: "rounded-full",
  size: {
    xs: "px-2 py-1 text-xs",
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 ",
    lg: "px-8 py-3 text-lg",
  },
  variant: {
    primary:
      "bg-squawk-blue hover:bg-squawk-blue/80 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-50 text-white",
    secondary:
      "bg-gray-200 dark:bg-gray-700/40 hover:bg-gray-300 dark:hover:bg-gray-600 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-opacity-50 text-gray-700 dark:text-gray-300",
    danger:
      "bg-red-600 hover:bg-red-500 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-opacity-50 text-white",
  },
};

export const Button = React.forwardRef(
  (
    {
      type = "button",
      variant = "primary",
      disabled = false,
      size = "sm",
      onClick,
      className,
      children,
      loading,
      renderIcon,
    }: ButtonProps,
    ref
  ) => {
    const style = clsx(
      styles.base,
      styles.size[size],
      styles.variant[variant],
      disabled && styles.disabled,
      className
    );

    return (
      <HeadlessButton
        type={type}
        className={style}
        onClick={onClick}
        disabled={disabled}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        {/* show spinner */}
        {loading && <Spinner size={size} />}

        {/* show icon if provided */}
        {!loading && renderIcon && renderIcon()}

        {children}
      </HeadlessButton>
    );
  }
);
